import { loginRequestSchema, type LoginResponse, type PublicTenantDto } from '@an-crm/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowRight, Building2, Eye, EyeOff, ShieldUser } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { type FormEvent, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { friendlyMessage, isApiError } from '@/lib/api/api-error';
import { type FieldErrors, toFieldErrors } from '@/lib/forms';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { authApi } from './api';
import { FormAlert } from './form-alert';

export interface CredentialsFormProps {
  initialTenant: string;
  onResult: (result: LoginResponse, tenant: string | null) => void;
  onTenantResolved?: (tenant: PublicTenantDto | null) => void;
}

const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

export function CredentialsForm({ initialTenant, onResult, onTenantResolved }: CredentialsFormProps) {
  const [platform, setPlatform] = useState(false);
  const [tenant, setTenant] = useState(initialTenant);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});

  const slug = useDebouncedValue(tenant.trim().toLowerCase(), 350);
  const lookupEnabled = !platform && slug.length >= 2 && SLUG_RE.test(slug);
  const publicTenant = useQuery({
    queryKey: ['public-tenant', slug],
    queryFn: ({ signal }) => authApi.publicTenant(slug, signal),
    enabled: lookupEnabled,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const resolved = lookupEnabled ? (publicTenant.data ?? null) : null;
  useEffect(() => {
    onTenantResolved?.(resolved);
  }, [resolved, onTenantResolved]);

  const login = useMutation({
    mutationFn: authApi.login,
    onSuccess: (result, vars) => onResult(result, vars.tenant ?? null),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const parsed = loginRequestSchema.safeParse({ tenant: platform ? null : tenant, email, password });
    if (!parsed.success) {
      setErrors(toFieldErrors(parsed.error));
      return;
    }
    if (!platform && !parsed.data.tenant) {
      setErrors({ tenant: 'Enter your organisation' });
      return;
    }
    setErrors({});
    login.mutate(parsed.data);
  };

  const resolvedName = resolved ? (resolved.branding.displayName ?? resolved.name) : null;
  const tenantNotFound = lookupEnabled && isApiError(publicTenant.error) && publicTenant.error.status === 404;

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
      <div className="flex flex-col gap-1.5">
        <h1 className="font-display text-[28px] leading-9 font-semibold text-fg">
          {platform ? 'Platform sign in' : resolvedName ? `Sign in to ${resolvedName}` : 'Welcome back'}
        </h1>
        <p className="text-sm text-fg-muted">
          {platform
            ? 'Super Admin access to the Ankyah platform console.'
            : 'Sign in to your organisation’s workspace.'}
        </p>
      </div>

      <FormAlert message={login.error ? friendlyMessage(login.error) : null} />

      <AnimatePresence initial={false} mode="popLayout">
        {!platform && (
          <motion.div
            key="tenant"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2 }}
          >
            <Field
              label="Organisation"
              error={errors.tenant ?? (tenantNotFound ? 'We couldn’t find that organisation' : undefined)}
              hint={
                resolvedName ? `Signing in to ${resolvedName}` : 'Your organisation’s short name, e.g. acme'
              }
            >
              {(p) => (
                <div className="relative">
                  <Building2
                    className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-subtle"
                    aria-hidden
                  />
                  <Input
                    {...p}
                    name="tenant"
                    autoComplete="organization"
                    autoCapitalize="none"
                    spellCheck={false}
                    placeholder="acme"
                    className="pl-9"
                    value={tenant}
                    onChange={(e) => setTenant(e.target.value)}
                  />
                </div>
              )}
            </Field>
          </motion.div>
        )}
      </AnimatePresence>

      <Field label="Email" error={errors.email}>
        {(p) => (
          <Input
            {...p}
            name="email"
            type="email"
            autoComplete="username"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        )}
      </Field>

      <Field label="Password" error={errors.password}>
        {(p) => (
          <div className="relative">
            <Input
              {...p}
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              className="pr-10"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              className="focus-ring absolute top-1/2 right-1.5 flex size-7 -translate-y-1/2 items-center justify-center rounded-md text-fg-subtle hover:text-fg"
            >
              {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          </div>
        )}
      </Field>

      <Button type="submit" size="lg" loading={login.isPending} className="mt-1 w-full">
        Sign in <ArrowRight aria-hidden />
      </Button>

      <div className="flex items-center justify-center border-t border-border pt-4">
        <button
          type="button"
          onClick={() => {
            setPlatform((p) => !p);
            setErrors({});
            login.reset();
          }}
          className="focus-ring inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[13px] font-medium text-fg-muted transition-colors hover:text-primary-text"
        >
          {platform ? (
            <>
              <Building2 className="size-3.5" aria-hidden /> Sign in to an organisation instead
            </>
          ) : (
            <>
              <ShieldUser className="size-3.5" aria-hidden /> Platform admin sign in
            </>
          )}
        </button>
      </div>
    </form>
  );
}
